import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { ExternalLink, Mail, User } from 'lucide-react';
interface SettingsCustomerPortalPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function SettingsCustomerPortalPage({
  currentPage = 'Settings',
  onNavigate
}: SettingsCustomerPortalPageProps) {
  const navigate = useNavigate();
  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Section 1: Subscription Controls
  const [allowCancel, setAllowCancel] = useState(true);
  const [allowPause, setAllowPause] = useState(true);
  const [allowSkip, setAllowSkip] = useState(true);
  const [allowProcessNow, setAllowProcessNow] = useState(true);
  // Skip limit controls
  const [skipMaxLimit, setSkipMaxLimit] = useState('Unlimited');
  const [skipPeriod, setSkipPeriod] = useState('Year');
  const [requireSkipReason, setRequireSkipReason] = useState(false);
  const [skipLimitMessage, setSkipLimitMessage] = useState("You've reached your skip limit for this period. Please contact us if you need assistance.");
  // Section 2: Magic Link Email
  const [emailSubject, setEmailSubject] = useState('Access Your Wine Club Portal');
  const [buttonText, setButtonText] = useState('Get Magic Link');
  const [placeholderText, setPlaceholderText] = useState('Enter your email address');
  const [successMsg, setSuccessMsg] = useState("Check your email! We've sent you a secure login link.");
  // Navigation handler
  const handleNavigate = onNavigate || ((page: string) => {
    if (page === 'Dashboard') navigate('/');else if (page === 'Subscriptions') navigate('/subscriptions');else if (page === 'Orders') navigate('/orders');else if (page === 'Contacts' || page === 'Contact') navigate('/contacts');else if (page === 'Club Builder') navigate('/club-builder');else if (page === 'Products') navigate('/products');else if (page === 'Tools') navigate('/management/tools');else if (page === 'Tag Management' || page === 'Tags') navigate('/settings/tags');else if (page === 'Settings' || page === 'General') navigate('/settings/general');else if (page === 'Order Processing') navigate('/settings/order-processing');else if (page === 'Customer Portal') navigate('/settings/customer-portal');else if (page === 'Account and Billing') navigate('/settings/account-billing');else navigate('/');
  });
  // Save handler (simulated)
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  // Toggle handler
  const handleToggle = (setter: React.Dispatch<React.SetStateAction<boolean>>, value: boolean, settingName: string) => {
    setter(value);
    saveSetting();
  };
  // Input handler (with debounce simulation)
  const handleInputBlur = () => {
    saveSetting();
  };
  // Preview Portal
  const handlePreviewPortal = () => {
    window.open('https://portal.winepik.com/demo', '_blank');
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-8954">
      {/* NavBar */}
      <div className="mb-6" data-id="element-8955">
        <NavBar tabs={[]} title="Customer Portal" data-id="element-8956" />
      </div>

      {/* Page Header - Subtitle + Preview Button */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-8957">
        <p className="text-[14px] text-[#64748B]" data-id="element-8958">
          Configure the customer-facing subscription portal
        </p>
        <Button variant="secondary" onClick={handlePreviewPortal} iconRight={<ExternalLink className="w-4 h-4" data-id="element-8960" />} data-id="element-8959">
          Preview Portal
        </Button>
      </div>

      {/* Main Content - Full Width Cards */}
      <div className="space-y-6" data-id="element-8961">
        {/* Section 1: Subscription Controls */}
        <Card padding="none" data-id="element-8962">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-8963">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-8964">
              <User className="w-5 h-5 text-[#0EA5E9]" data-id="element-8965" />
            </div>
            <div data-id="element-8966">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8967">
                Subscription Controls
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8968">
                Actions members can take on their subscriptions
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-8969">
            {/* Allow Cancel */}
            <div className="flex items-start justify-between" data-id="element-8970">
              <div className="flex-1 pr-6" data-id="element-8971">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8972">
                  Allow Cancel Subscription
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-8973">
                  Members can cancel their subscription from the portal
                </p>
              </div>
              <button onClick={() => handleToggle(setAllowCancel, !allowCancel, 'Allow Cancel')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${allowCancel ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={allowCancel} data-id="element-8974">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowCancel ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8975" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-8976" />

            {/* Allow Pause */}
            <div className="flex items-start justify-between" data-id="element-8977">
              <div className="flex-1 pr-6" data-id="element-8978">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8979">
                  Allow Pause Subscription
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-8980">
                  Members can temporarily pause their subscription
                </p>
              </div>
              <button onClick={() => handleToggle(setAllowPause, !allowPause, 'Allow Pause')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${allowPause ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={allowPause} data-id="element-8981">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowPause ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8982" />
              </button>
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-8983" />

            {/* Allow Skip */}
            <div data-id="element-8984">
              <div className="flex items-start justify-between" data-id="element-8985">
                <div className="flex-1 pr-6" data-id="element-8986">
                  <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8987">
                    Allow Skip Next Order
                  </h3>
                  <p className="text-[14px] text-[#64748B]" data-id="element-8988">
                    Members can skip their upcoming order without pausing
                  </p>
                </div>
                <button onClick={() => handleToggle(setAllowSkip, !allowSkip, 'Allow Skip')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${allowSkip ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={allowSkip} data-id="element-8989">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowSkip ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8990" />
                </button>
              </div>

              {/* Skip Limit Controls (Nested) */}
              {allowSkip && <div className="mt-4 pl-4 border-l-4 border-[#0EA5E9] bg-[#F8FAFC] p-4 space-y-4" data-id="element-8991">
                  {/* Row 1: Maximum skips */}
                  <div data-id="element-8992">
                    <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-8993">
                      Maximum skips
                    </label>
                    <div className="flex items-center gap-3" data-id="element-8994">
                      <select value={skipMaxLimit} onChange={e => {
                    setSkipMaxLimit(e.target.value);
                    saveSetting();
                  }} className="h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-8995">
                        <option value="1" data-id="element-8996">1</option>
                        <option value="2" data-id="element-8997">2</option>
                        <option value="3" data-id="element-8998">3</option>
                        <option value="4" data-id="element-8999">4</option>
                        <option value="5" data-id="element-9000">5</option>
                        <option value="Unlimited" data-id="element-9001">Unlimited</option>
                      </select>
                      <span className="text-[14px] text-[#64748B]" data-id="element-9002">per</span>
                      <select value={skipPeriod} onChange={e => {
                    setSkipPeriod(e.target.value);
                    saveSetting();
                  }} className="h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-9003">
                        <option value="Quarter" data-id="element-9004">Quarter</option>
                        <option value="6 Months" data-id="element-9005">6 Months</option>
                        <option value="Year" data-id="element-9006">Year</option>
                        <option value="Rolling 12 Months" data-id="element-9007">
                          Rolling 12 Months
                        </option>
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Require skip reason */}
                  <div className="flex items-start justify-between" data-id="element-9008">
                    <div className="flex-1 pr-6" data-id="element-9009">
                      <h4 className="text-[14px] font-medium text-[#0F172A] mb-1" data-id="element-9010">
                        Require skip reason
                      </h4>
                      <p className="text-[13px] text-[#64748B]" data-id="element-9011">
                        Members must select a reason when skipping
                      </p>
                    </div>
                    <button onClick={() => handleToggle(setRequireSkipReason, !requireSkipReason, 'Require Skip Reason')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${requireSkipReason ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={requireSkipReason} data-id="element-9012">
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${requireSkipReason ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9013" />
                    </button>
                  </div>

                  {/* Row 3: Limit reached message (only if not Unlimited) */}
                  {skipMaxLimit !== 'Unlimited' && <div data-id="element-9014">
                      <label className="block text-[14px] font-medium text-[#475569] mb-2" data-id="element-9015">
                        Limit reached message
                      </label>
                      <textarea value={skipLimitMessage} onChange={e => setSkipLimitMessage(e.target.value)} onBlur={handleInputBlur} placeholder="You've reached your skip limit for this period. Please contact us if you need assistance." rows={3} className="w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow resize-none" data-id="element-9016" />
                      <p className="mt-1 text-[12px] text-[#64748B]" data-id="element-9017">
                        Displayed when member has used all available skips
                      </p>
                    </div>}
                </div>}
            </div>

            <div className="h-px bg-[#E2E8F0]" data-id="element-9018" />

            {/* Allow Process Now */}
            <div className="flex items-start justify-between" data-id="element-9019">
              <div className="flex-1 pr-6" data-id="element-9020">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-9021">
                  Allow Process Order Now
                </h3>
                <p className="text-[14px] text-[#64748B]" data-id="element-9022">
                  Members can trigger immediate processing of their next order
                </p>
              </div>
              <button onClick={() => handleToggle(setAllowProcessNow, !allowProcessNow, 'Allow Process Now')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${allowProcessNow ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={allowProcessNow} data-id="element-9023">
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowProcessNow ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-9024" />
              </button>
            </div>
          </div>
        </Card>

        {/* Section 2: Magic Link Email */}
        <Card padding="none" data-id="element-9025">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center gap-3" data-id="element-9026">
            <div className="p-2 bg-[#E0F2FE] rounded-lg" data-id="element-9027">
              <Mail className="w-5 h-5 text-[#0EA5E9]" data-id="element-9028" />
            </div>
            <div data-id="element-9029">
              <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-9030">
                Magic Link Email
              </h2>
              <p className="text-[14px] text-[#64748B] mt-1" data-id="element-9031">
                Customize the passwordless login email text
              </p>
            </div>
          </div>

          <div className="p-6 space-y-6" data-id="element-9032">
            {/* Email Subject */}
            <div data-id="element-9033">
              <div className="flex justify-between mb-2" data-id="element-9034">
                <label className="text-[14px] font-medium text-[#475569]" data-id="element-9035">
                  Email Subject
                </label>
                <span className="text-[12px] text-[#94A3B8]" data-id="element-9036">
                  {emailSubject.length}/100
                </span>
              </div>
              <input type="text" maxLength={100} value={emailSubject} onChange={e => setEmailSubject(e.target.value)} onBlur={handleInputBlur} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-9037" />
            </div>

            {/* Button Text */}
            <div data-id="element-9038">
              <div className="flex justify-between mb-2" data-id="element-9039">
                <label className="text-[14px] font-medium text-[#475569]" data-id="element-9040">
                  Retrieve Link Button Text
                </label>
                <span className="text-[12px] text-[#94A3B8]" data-id="element-9041">
                  {buttonText.length}/40
                </span>
              </div>
              <input type="text" maxLength={40} value={buttonText} onChange={e => setButtonText(e.target.value)} onBlur={handleInputBlur} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-9042" />
            </div>

            {/* Placeholder Text */}
            <div data-id="element-9043">
              <div className="flex justify-between mb-2" data-id="element-9044">
                <label className="text-[14px] font-medium text-[#475569]" data-id="element-9045">
                  Email Address Placeholder
                </label>
                <span className="text-[12px] text-[#94A3B8]" data-id="element-9046">
                  {placeholderText.length}/60
                </span>
              </div>
              <input type="text" maxLength={60} value={placeholderText} onChange={e => setPlaceholderText(e.target.value)} onBlur={handleInputBlur} className="w-full h-[40px] px-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow" data-id="element-9047" />
            </div>

            {/* Success Message */}
            <div data-id="element-9048">
              <div className="flex justify-between mb-2" data-id="element-9049">
                <label className="text-[14px] font-medium text-[#475569]" data-id="element-9050">
                  Success Message
                </label>
                <span className="text-[12px] text-[#94A3B8]" data-id="element-9051">
                  {successMsg.length}/200
                </span>
              </div>
              <textarea maxLength={200} rows={2} value={successMsg} onChange={e => setSuccessMsg(e.target.value)} onBlur={handleInputBlur} className="w-full p-3 border border-[#CBD5E1] rounded-md bg-white text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent transition-shadow resize-none" data-id="element-9052" />
            </div>

            {/* Preview Panel */}
            <div className="p-6 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-id="element-9053">
              <h4 className="text-[12px] font-semibold text-[#64748B] uppercase tracking-wider mb-4" data-id="element-9054">
                Login Form Preview
              </h4>
              <div className="max-w-sm mx-auto bg-white p-6 rounded-lg shadow-sm border border-[#E2E8F0]" data-id="element-9055">
                <h3 className="text-lg font-semibold text-center mb-4" data-id="element-9056">
                  Welcome Back
                </h3>
                <div className="space-y-4" data-id="element-9057">
                  <input disabled placeholder={placeholderText} className="w-full h-10 px-3 border border-[#CBD5E1] rounded-md bg-white" data-id="element-9058" />
                  <button disabled className="w-full h-10 rounded-md font-medium bg-[#0EA5E9] text-white" data-id="element-9059">
                    {buttonText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-9060">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-9061">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-9062" />
            <div className="flex-1" data-id="element-9063">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-9064">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-9065">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}